import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, ArrowRight, Images, ChevronDown } from "lucide-react";
import {
  MODES,
  modosVisibles,
  bajadaCortaModo,
  caratulaModo,
  nombreCortoModo,
  type ModeCard,
} from "./v2/modesCatalog";
import { getModeExamples, type ModeExample } from "./v2/modeExamples";
import { ModeExamplesModal } from "./v2/components/ModeExamplesModal";
import { setupAutoplayVideo, ModeLockOverlay } from "./v2/components/HomeCards";
import { getUserPlan } from "../lib/planUtils";
import {
  getModePlanLock,
  getPlanRequirementNote,
  type ModePlanLock,
} from "../lib/planCapabilities";
import { FeatureLockedModal } from "../components/FeatureLockedModal";
import { EstudioVideoCover } from "../components/EstudioVideoCover";
import { HERRAMIENTAS_EXTRA } from "./estilos/contenidoAtlas";
import { DesktopOnlyModal } from "../components/DesktopOnlyModal";
import { useUserGroup } from "../hooks/useUserGroup";
import { FLUJOS_ENABLED } from "./flujos/flags";
import { useRubroMarcaActiva } from "../hooks/useRubroMarca";
import {
  esRubroBelleza,
  esRubroHogarDeco,
  esRubroModa,
  rubroVePlantillas,
  rubroVePlantillasBelleza,
} from "../lib/rubro";

/** La carátula de Flujos —clip y poster—, desde la única entrada que la herramienta tiene. */
const CARATULA_FLUJOS = (() => {
  const flujos = HERRAMIENTAS_EXTRA.find((h) => h.id === "flujos");
  return { poster: flujos?.poster ?? "", video: flujos?.video };
})();

/**
 * Las dos secciones de la pantalla, en el MISMO orden y con los MISMOS rótulos que
 * los grupos de la sidebar — ADR-2026-08-31-la-sidebar-agrupa-por-video-e-imagen.
 * Ésta es la pantalla a la que lleva el "Ver más" de cada grupo: si acá se llamaran
 * distinto, el usuario llegaría buscando un grupo que no existe.
 */
const SECCIONES_MODOS: { familia: "video" | "imagen"; rotulo: string }[] = [
  { familia: "imagen", rotulo: "Modos de imagen" },
  { familia: "video", rotulo: "Modos de video" },
];

/** Rótulo de una familia, para no repetir el `.find` en cada uso. */
const rotuloFamilia = (familia: "video" | "imagen") =>
  SECCIONES_MODOS.find((s) => s.familia === familia)!.rotulo;

/**
 * Pantalla "Modos" — lista los modos de generación de Interfaz V2 (los mismos
 * del design-row de la home), uno abajo del otro, cada uno con su video,
 * título y descripción. Al tocar uno se entra a ese modo en V2.
 */
export const Modos = () => {
  const navigate = useNavigate();
  /**
   * `?familia=video|imagen` — con qué sección se entró a la pantalla.
   * Lo pone el "Ver más" de cada grupo de la sidebar
   * (ADR-2026-08-31-ver-mas-de-la-sidebar-enfoca-su-familia): el usuario tocó "Ver
   * más" DENTRO de "Video", así que la pantalla abre con los modos de video y deja
   * los de imagen plegados abajo, en vez de aterrizar en una lista de diez cards
   * donde su grupo es la mitad de arriba o la de abajo según cuál tocó.
   *
   * Sin el parámetro (tab "Modos" de mobile, ítem del sidebar viejo) NO hay sección
   * elegida y se muestran las dos abiertas, como siempre.
   */
  const [searchParams] = useSearchParams();
  const familiaParam = searchParams.get("familia");
  const familiaFoco: "video" | "imagen" | null =
    familiaParam === "video" || familiaParam === "imagen" ? familiaParam : null;
  /**
   * La OTRA familia, plegada. Arranca cerrada SIEMPRE —incluso si se vuelve a la
   * pantalla— porque el estado vive en el montaje y el `useEffect` lo resetea
   * cuando cambia la familia enfocada (tocar "Ver más" de Imagen estando en
   * `?familia=video` no remonta la pantalla, sólo cambia el query).
   */
  const [otraFamiliaAbierta, setOtraFamiliaAbierta] = useState(false);
  useEffect(() => {
    setOtraFamiliaAbierta(false);
  }, [familiaFoco]);
  // Modos `devOnly` (ej. "Video con influencer") solo se listan para dev testers.
  const { isDevTester } = useUserGroup();
  // Modos `soloHogarDeco` (catálogo de muebles + infografías): se listan solo si
  // la MARCA activa es del rubro "Hogar y Decoración". Mientras el rubro no se
  // conoce (`null`), NO se muestran — el cache de localStorage del hook evita
  // que aparezcan un frame tarde para quien sí califica.
  //
  // Este hook reemplaza al `useBrandProfile` que usaba el rediseño para lo mismo
  // (ADR-2026-08-05-un-solo-gate-de-rubro-para-los-modos). La diferencia práctica:
  // `useRubroMarcaActiva` NO cae al rubro de la empresa — con la marca sin rubro los
  // modos de muebles no se muestran, que es la decisión explícita de la principal.
  const { rubro: rubroMarca } = useRubroMarcaActiva();
  const esHogarDeco = esRubroHogarDeco(rubroMarca);
  const esBelleza = esRubroBelleza(rubroMarca);
  const esModa = esRubroModa(rubroMarca);
  // La card de Plantillas la ven Moda y Belleza, y cada uno abre SU pantalla: es una sola
  // card porque el nombre, la bajada y la carátula son los mismos, y los dos rubros son
  // excluyentes (ADR-2026-09-10-plantillas-belleza-modo-propio).
  const rutaPlantillas: string | null = rubroVePlantillas(rubroMarca)
    ? "/interfaz-v2/plantillas"
    : rubroVePlantillasBelleza(rubroMarca)
      ? "/interfaz-v2/plantillas-belleza"
      : null;
  // La carátula de Catálogo sigue al rubro — ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro.
  const rubro = rubroMarca;

  // Plan del usuario para gatear el acceso a los modos. Arrancamos del cache
  // de localStorage (mismo que llena el Sidebar) para tener el plan al instante
  // y refrescamos con el valor real. Si todavía no se conoce, no bloqueamos.
  const [plan, setPlan] = useState<string | null>(() => {
    try {
      return localStorage.getItem("atlas-user-plan");
    } catch {
      return null;
    }
  });
  useEffect(() => {
    let cancelled = false;
    // El plan real, sin lentes: el panel CONTEXTO DEV y sus overrides se eliminaron
    // (supersede ADR-2026-08-03-el-plan-simulado-tambien-gatea).
    getUserPlan().then((p) => {
      if (!cancelled && p) setPlan(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  // Lock a mostrar cuando el plan no alcanza para el modo tocado.
  const [modeLock, setModeLock] = useState<ModePlanLock | null>(null);

  // Visor de ejemplos ("Ver ejemplos"): modo + su lista, o null si cerrado.
  // CTA del visor: "Generar" (entra al modo) o, si el modo está bloqueado
  // para el plan, "Ver planes" (navega a /subscription) + una nota de qué
  // plan requiere. Se calcula al abrir.
  const [examplesModal, setExamplesModal] = useState<{
    title: string;
    description?: string;
    examples: ModeExample[];
    actionLabel: string;
    onAction: () => void;
    locked: boolean;
    planNote?: string;
  } | null>(null);

  // Nota del plan requerido: compartida con la home V2 (planCapabilities).
  const planRequirementNote = getPlanRequirementNote;

  // Card "Estudio de Video" (editor pro): Pro y Ultra (era Ultra hasta
  // ADR-2026-10-11-el-estudio-de-video-es-de-pro-y-ultra). Los dev testers lo ven
  // desbloqueado (igual que en el Sidebar). Si el plan no alcanza → candado +
  // modal compartidos. Ver ADR-526.
  //
  // Solo bloqueamos cuando sabemos con CERTEZA que no es dev tester
  // (isDevTester === false). Mientras el hook no resolvió (null), NO bloqueamos
  // — mismo criterio que getModePlanLock con el plan desconocido: así un dev
  // tester no ve un flash de candado en el primer login (antes de que cargue el
  // cache de grupo).
  const videoStudioLock =
    isDevTester === false ? getModePlanLock("editor-video", plan) : null;

  // Popup "solo desktop": la card se muestra también en mobile (para que un
  // Ultra descubra la feature desde el celu), pero el editor pro solo corre en
  // pantallas grandes → al tocarla en mobile se le avisa que la use en la compu.
  const [showVideoDesktopOnly, setShowVideoDesktopOnly] = useState(false);

  const handleVideoStudioClick = () => {
    // No-Ultra (cualquier dispositivo): visor de ejemplos con nota del plan +
    // CTA "Ver planes"; sin ejemplos cargados, el modal clásico.
    if (videoStudioLock) {
      const studioExamples = getModeExamples("editor-video");
      if (studioExamples.length > 0) {
        setExamplesModal({
          title: "Estudio de Video",
          description:
            "Editá tus videos con IA — timeline, cortes y voces en un mismo lugar.",
          examples: studioExamples,
          actionLabel: "Ver planes",
          onAction: () => navigate("/subscription"),
          locked: true,
          planNote: planRequirementNote(videoStudioLock.feature),
        });
      } else {
        setModeLock(videoStudioLock);
      }
      return;
    }
    // Habilitado, pero en el celular: avisar que se usa desde la computadora
    // (mismo umbral que el resto del proyecto: < 768px = teléfono).
    if (window.matchMedia("(max-width: 767px)").matches) {
      setShowVideoDesktopOnly(true);
      return;
    }
    navigate("/editor-video");
  };

  /**
   * Los modos visibles, partidos por familia. La regla es `preset.outputType`, la
   * MISMA que usan la sidebar y el sheet de Generar de mobile —
   * ADR-2026-08-31-la-sidebar-agrupa-por-video-e-imagen. Si acá se escribiera una
   * lista de paths aparte, un modo nuevo podría caer en "Video" en la sidebar y en
   * "Imagen" en esta pantalla.
   */
  const modosPorFamilia = useMemo(() => {
    // El filtro de visibilidad estaba escrito acá y en otros tres lugares; ahora es
    // `modosVisibles()` — ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares.
    // Esta pantalla NO pasa `esModelo`: no se abre desde un modelo.
    const visibles = modosVisibles(MODES, { isDevTester, esHogarDeco, esBelleza, esModa });
    return {
      video: visibles.filter((m) => m.preset.outputType === "video"),
      imagen: visibles.filter((m) => m.preset.outputType !== "video"),
    };
  }, [isDevTester, esHogarDeco, esBelleza, esModa]);

  /** Una card de modo. Extraída del grid para poder dibujarla en las dos secciones. */
  const tarjetaModo = (mode: ModeCard) => {
    // Lock por plan del modo (null si está permitido). Se usa para el
    // candado sobre la carátula y para el gating al tocar.
    const lock = getModePlanLock(mode.preset.kind, plan);
    // Ejemplos del modo (si no tiene, no se muestra "Ver ejemplos").
    const examples = getModeExamples(mode.preset.kind);
    // Navegamos con `presetData` en el state para que InterfazV2 abra el
    // sheet del modo en el primer paint (mecanismo `initialNavPreset`) y
    // NO se vea un flash de la home V2 al montar.
    const goToMode = () => {
      // Gating por plan: si el modo está bloqueado para el plan actual,
      // NO entramos — abrimos el visor de ejemplos (nota del plan +
      // CTA "Ver planes"); si el modo no tiene ejemplos cargados,
      // cae al modal clásico de "función exclusiva".
      if (lock) {
        if (examples.length > 0) {
          openExamples();
        } else {
          setModeLock(lock);
        }
        return;
      }
      // Modos con pantalla propia fuera de InterfazV2 (ej. Shorts Studio) declaran `route`.
      if (mode.route) {
        navigate(mode.route);
        return;
      }
      navigate(`/interfaz-v2/modo/${mode.path}`, {
        state: { presetData: mode.preset, collectionPresets: [] },
      });
    };
    // Payload del visor de ejemplos (lo comparten el botón "Ver
    // ejemplos" y el tap sobre una card bloqueada).
    const openExamples = () =>
      setExamplesModal({
        title: titulo,
        description: mode.preset.description,
        examples,
        actionLabel: lock ? "Ver planes" : "Generar",
        onAction: lock ? () => navigate("/subscription") : goToMode,
        locked: !!lock,
        planNote: lock ? planRequirementNote(lock.feature) : undefined,
      });
    // El nombre, la bajada y la carátula salen del catálogo, igual que en la home, la
    // sidebar y los sheets — ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo.
    const caratula = caratulaModo(mode.path, rubro, mode.preset.imageUrl);
    const titulo = nombreCortoModo(mode.path, mode.preset.title);
    const bajada = bajadaCortaModo(mode.path, mode.preset.description ?? "");
    return (
      <div
        key={mode.path}
        onClick={goToMode}
        className="w-full text-left group cursor-pointer flex flex-col"
      >
        {/* Carátula en video, 4 esquinas redondeadas (igual que la card
            de modo en la home V2). */}
        <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
          {/* La carátula propia del rubro gana sobre la del modo y se pinta como
              IMAGEN, sin el video (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro):
              el video de Catálogo es la grilla de moda, o sea justo lo que la
              portada del rubro viene a reemplazar. */}
          {caratula.video ? (
            <video
              ref={setupAutoplayVideo}
              src={caratula.video}
              poster={caratula.poster || undefined}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-label={titulo}
              className="absolute inset-0 w-full h-full object-cover sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
          ) : (
            // Modos sin video propio (catálogo-muebles / infografía):
            // imagen estática de la card. Ver ADR-817.
            <img
              src={caratula.poster}
              alt={titulo}
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
          )}
          {lock && <ModeLockOverlay />}
          {/* Botones sobre el video (abajo a la derecha): "Ver ejemplos"
              (si el modo tiene ejemplos) + "Generar". Si el modo está
              bloqueado, Generar se oculta (manda el candado + el modal) y
              Ver ejemplos queda solo en su lugar — el z-[3] lo deja
              tocable por encima del ModeLockOverlay (z-[2],
              pointer-events-none). */}
          <div className="absolute bottom-3 right-3 z-[3] flex items-center gap-2">
            {examples.length > 0 && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  openExamples();
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/90 hover:bg-white backdrop-blur text-[#02062B] text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
              >
                <Images className="w-4 h-4" />
                Ver ejemplos
              </button>
            )}
            {!lock && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToMode();
                }}
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
              >
                <Sparkles className="w-4 h-4" />
                Generar
              </button>
            )}
          </div>
        </div>
        {/* Texto debajo del video — sin chrome, igual que en la home. */}
        <div className="mt-3 flex flex-col gap-0.5">
          <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
            {titulo}
          </h3>
          <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
            {bajada}
          </p>
        </div>
      </div>
    );
  };

  /* Card "Plantillas" — primer elemento, SOLO desktop (`hidden sm:flex`).
      Lleva a la página de plantillas y reusa la misma imagen que el banner
      de plantillas de la home V2 desktop. Mismo estilo que las cards de
      modos (carátula 3:2 + pill + título/descripción) para un grid
      uniforme, pero con imagen estática en vez de video.

      Sólo la ve el rubro Moda — ADR-2026-08-03-plantillas-solo-para-moda.
      Esta condición reemplaza al `!esHogarDeco` de `principal`
      (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo), que apuntaba a lo
      mismo desde el otro lado: las plantillas son escenas de indumentaria y no
      le servían ni a muebles ni a gastronomía ni a cosmética. `rubroVePlantillas`
      es más estricto y cubre el caso de Hogar y Decoración igual, así que no
      quedan dos reglas discutiendo — ADR-2026-08-05-plantillas-solo-moda-gana-al-merge. */
  const tarjetaPlantillas = rutaPlantillas ? (
        <div
          onClick={() => navigate(rutaPlantillas)}
          className="hidden sm:flex w-full text-left group cursor-pointer flex-col"
        >
          <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
            <img
              src={caratulaModo("plantillas").poster}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                navigate(rutaPlantillas);
              }}
              className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
            >
              <ArrowRight className="w-4 h-4" />
              Ver plantillas
            </button>
          </div>
          <div className="mt-3 flex flex-col gap-0.5">
            <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
              {nombreCortoModo("plantillas")}
            </h3>
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              {bajadaCortaModo("plantillas")}
            </p>
          </div>
        </div>
  ) : null;

  /* Card "Estudio de Video" — editor pro. Caso especial fuera de los
      modos V2 (igual que "Plantillas"). Se muestra en TODOS los tamaños:
      en desktop navega a /editor-video; en mobile un Ultra ve un aviso de
      "usalo desde la compu"; los no-Ultra ven el candado + modal Ver
      planes. Reusa ModeLockOverlay + FeatureLockedModal compartidos. */
  const tarjetaEstudioVideo = (
        <div
          onClick={handleVideoStudioClick}
          className="w-full text-left group cursor-pointer flex flex-col"
        >
          <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
            <EstudioVideoCover />
            {videoStudioLock && <ModeLockOverlay />}
            {/* Botones sobre la carátula: "Ver ejemplos" (si hay cargados para
                'editor-video') + "Generar" (oculto si está bloqueado — ahí
                manda el candado + el modal; Ver ejemplos queda en su lugar). */}
            <div className="absolute bottom-3 right-3 z-[3] flex items-center gap-2">
              {getModeExamples("editor-video").length > 0 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExamplesModal({
                      title: "Estudio de Video",
                      description:
                        "Editá tus videos con IA — timeline, cortes y voces en un mismo lugar.",
                      examples: getModeExamples("editor-video"),
                      actionLabel: videoStudioLock ? "Ver planes" : "Generar",
                      onAction: videoStudioLock
                        ? () => navigate("/subscription")
                        : handleVideoStudioClick,
                      locked: !!videoStudioLock,
                      planNote: videoStudioLock
                        ? planRequirementNote(videoStudioLock.feature)
                        : undefined,
                    });
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white/90 hover:bg-white backdrop-blur text-[#02062B] text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                >
                  <Images className="w-4 h-4" />
                  Ver ejemplos
                </button>
              )}
              {!videoStudioLock && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleVideoStudioClick();
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                >
                  <Sparkles className="w-4 h-4" />
                  Generar
                </button>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-col gap-0.5">
            <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
              {nombreCortoModo("editor-video")}
            </h3>
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              {bajadaCortaModo("editor-video")}
            </p>
          </div>
        </div>
  );

  /* Card "Flujos" — el canvas de nodos. La tercera herramienta que no es un modo de
     `MODES` (con Plantillas y el Estudio de Video), y hasta ahora la ÚNICA sin card
     acá: se llegaba sólo por el ítem de la sidebar —
     ADR-2026-09-19-flujos-tiene-card-en-modos.

     Que faltara no importaba mientras la sidebar la mostrara siempre; desde que esa
     lista son los dos modos MÁS USADOS
     (ADR-2026-09-19-la-sidebar-muestra-los-dos-modos-mas-usados), Flujos se cae del
     cupo apenas usás otras dos cosas y quedaba sin ninguna puerta en toda la app.

     `devOnly` es el kill-switch del flag: con `FLUJOS_ENABLED` en `false` la card la
     ven sólo las cuentas del equipo, el mismo trato que le da la sidebar. El piso de
     plan (Pro/Ultra) es aparte y lo pone `getModePlanLock('flujos')`, igual que el
     candado del ítem. */
  const flujosVisibleEnModos = FLUJOS_ENABLED || isDevTester === true;
  const flujosLock =
    isDevTester === false ? getModePlanLock("flujos", plan) : null;

  const handleFlujosClick = () => {
    // Sin plan: el modal compartido de feature bloqueada, igual que cualquier modo
    // con candado de esta pantalla. Flujos no tiene ejemplos cargados en
    // `modeExamples`, así que no hay visor que ofrecer antes.
    if (flujosLock) {
      setModeLock(flujosLock);
      return;
    }
    navigate("/flujos");
  };

  const tarjetaFlujos = flujosVisibleEnModos ? (
        <div
          onClick={handleFlujosClick}
          className="w-full text-left group cursor-pointer flex flex-col"
        >
          <div className="relative aspect-[3/2] bg-neutral-100 rounded-2xl overflow-hidden">
            {/* Carátula EN VIDEO — ADR-2026-09-22-la-caratula-de-flujos-pasa-a-video.
                Supersede al `<img>` de ADR-2026-09-22-cada-modo-con-su-caratula y, antes,
                al gráfico navy de ADR-2026-09-19-flujos-tiene-card-en-modos.

                Mismo `setupAutoplayVideo` que las cards de modo: reproduce solo, en
                silencio y en loop, y el `poster` (el cuadro 0 del propio clip) es lo que
                se ve mientras carga. Las dos rutas salen de `HERRAMIENTAS_EXTRA`, donde ya
                vive el resto de la herramienta, para que esta card y la sidebar no puedan
                mostrar cosas distintas. */}
            <video
              ref={setupAutoplayVideo}
              src={CARATULA_FLUJOS.video}
              poster={CARATULA_FLUJOS.poster}
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover sm:group-hover:scale-[1.03] transition-transform duration-500 ease-out"
            />
            {flujosLock && <ModeLockOverlay />}
            {!flujosLock && (
              <div className="absolute bottom-3 right-3 z-[3] flex items-center gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlujosClick();
                  }}
                  className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-brand-primary hover:bg-brand-primary-hover text-white text-[14px] font-semibold shadow-lg active:scale-[0.97] transition-transform"
                >
                  <Sparkles className="w-4 h-4" />
                  Armar flujo
                </button>
              </div>
            )}
          </div>
          <div className="mt-3 flex flex-col gap-0.5">
            <h3 className="text-[15.5px] sm:text-[17px] font-semibold text-brand-black/90 leading-tight transition-colors duration-300 sm:group-hover:text-brand-primary">
              Flujos
            </h3>
            <p className="text-[13px] sm:text-[13.5px] text-[#3C3C43]/60 leading-snug">
              Encadená nodos: producto → prompt → imagen → voz → video, y volvé a
              correrlo con otro producto.
            </p>
          </div>
        </div>
  ) : null;

  /** La familia contraria — la que baja al desplegable del pie. */
  const otraFamilia = (familia: "video" | "imagen") =>
    familia === "video" ? "imagen" : "video";

  /**
   * La grilla de cards de una familia (modos + su card especial: Estudio de Video
   * en video, Plantillas en imagen). Devuelve `null` si la familia quedó sin nada
   * —el rubro filtró las plantillas y el plan bloqueó el resto—, y ahí ni el
   * título ni el desplegable se dibujan.
   *
   * Está extraída porque ahora la misma grilla se pinta en dos lugares: abierta
   * como sección, o adentro del desplegable de la otra familia.
   *
   * Mobile: una columna apilada. Desktop: 2 columnas en pantallas medianas, 3 en
   * anchas.
   */
  const gridDeFamilia = (familia: "video" | "imagen") => {
    const modos = modosPorFamilia[familia];
    const extras =
      familia === "video" ? tarjetaEstudioVideo ?? tarjetaFlujos : tarjetaPlantillas;
    if (modos.length === 0 && !extras) return null;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 sm:gap-x-6 gap-y-7 sm:gap-y-8">
        {familia === "imagen" && tarjetaPlantillas}
        {modos.map(tarjetaModo)}
        {familia === "video" && tarjetaEstudioVideo}
        {/* Flujos cierra la familia de video, detrás del Estudio: las dos son
            herramientas y no modos, y ésta además puede no estar (flag apagado). */}
        {familia === "video" && tarjetaFlujos}
      </div>
    );
  };

  /**
   * Las dos grillas cuando se entra enfocado. Se calculan una sola vez: la de la
   * otra familia se arma aunque esté plegada —crear el elemento no monta nada— y
   * sirve para saber si el desplegable tiene algo que mostrar.
   *
   * Si la familia del query quedó vacía (rubro + plan filtraron todo) el foco se
   * cae y la pantalla vuelve a las dos secciones abiertas: mejor eso que un título
   * con nada abajo.
   */
  const gridFoco = familiaFoco ? gridDeFamilia(familiaFoco) : null;
  const gridOtra = familiaFoco ? gridDeFamilia(otraFamilia(familiaFoco)) : null;
  const rotuloOtra = familiaFoco
    ? rotuloFamilia(otraFamilia(familiaFoco))
    : "";
  /**
   * Vista previa del desplegable — ADR-2026-08-31-el-desplegable-de-modos-muestra-lo-que-esconde.
   * Cerrado, la fila no puede ser sólo un rótulo: hay que poder VER qué hay adentro.
   * Las miniaturas son las mismas carátulas que después se abren (con la portada del
   * rubro ganando, igual que en la card), y el resumen nombra los primeros modos.
   */
  const modosOtra = familiaFoco ? modosPorFamilia[otraFamilia(familiaFoco)] : [];
  const miniaturasOtra = modosOtra
    .slice(0, 4)
    .map((m) => caratulaModo(m.path, rubro, m.preset.imageUrl).poster)
    .filter(Boolean);
  // El conteo incluye la card especial de esa familia (Plantillas / Estudio de
  // Video): está adentro del desplegable, así que tiene que estar en el número.
  const cantidadOtra =
    modosOtra.length +
    (familiaFoco === "video"
      ? tarjetaPlantillas
        ? 1
        : 0
      : // La otra es video: el Estudio siempre, y Flujos si el flag lo deja ver.
        1 + (tarjetaFlujos ? 1 : 0));
  const resumenOtra = (() => {
    const nombres = modosOtra.slice(0, 3).map((m) => nombreCortoModo(m.path, m.preset.title));
    const resto = cantidadOtra - nombres.length;
    return resto > 0
      ? `${nombres.join(", ")} y ${resto} más`
      : nombres.join(", ");
  })();

  return (
    <div className="px-4 sm:px-8 pt-2 pb-[calc(env(safe-area-inset-bottom,0px)+6rem)] md:pb-8 max-w-content mx-auto min-h-screen">
      {/* Sin título de pantalla — ADR-2026-08-31-modos-se-titula-por-seccion. Decía
          "Modos" arriba y después "Video" e "Imagen" en gris chiquito: tres
          encabezados para dos listas, y el que mandaba era el que menos información
          daba. Ahora los títulos de sección DICEN qué son ("Modos de video") y toman
          el tamaño que tenía el de pantalla.

          OJO: al no haber `<h1>`, el shell tampoco suma sus 40px de aire (la regla
          `:has(> h1)` de `index.css`), así que el primer título arranca a los 32px
          del tope de la columna. Es el mismo caso que `/referidos`
          (ADR-2026-08-28-referidos-mas-visual-2) y se compensa con el `pt` de la
          primera sección. */}
      {/* Con `?familia=` (el "Ver más" de la sidebar) manda esa sección y la otra baja
          plegada al pie — ADR-2026-08-31-ver-mas-de-la-sidebar-enfoca-su-familia.
          Sin el parámetro, las dos secciones abiertas.

          Dos secciones, "Video" e "Imagen" — ADR-2026-08-31-la-sidebar-agrupa-por-video-e-imagen.
          Antes era un grid único con los diez modos mezclados y sin ningún corte: para
          encontrar "el de video" había que leer las diez descripciones. Los separadores
          son los MISMOS rótulos que los grupos de la sidebar, porque ésta es la pantalla
          a la que lleva su "Ver más" — si acá se llamaran distinto, el usuario llegaría
          buscando un grupo que no existe.

          Imagen va primero, igual que en la sidebar
          (ADR-2026-08-31-imagen-va-antes-que-video-en-la-sidebar): el orden de las dos
          listas tiene que ser el mismo o el usuario busca su grupo donde no está.

          Mobile: una columna apilada. Desktop: 2 columnas en pantallas medianas, 3 en
          anchas. */}
      {familiaFoco && gridFoco ? (
        <>
          {/* Entrada desde el "Ver más" de la sidebar: manda SU familia. La otra baja
              a un desplegable al pie, cerrado, para que siga estando a un toque sin
              competirle a la sección que el usuario pidió. */}
          <section className="mb-10 pt-2 sm:pt-4">
            <h2 className="mb-4 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black sm:text-[32px]">
              {rotuloFamilia(familiaFoco)}
            </h2>
            {gridFoco}
          </section>
          {gridOtra && (
            <section className="mb-10">
              {/* El desplegable es una PIEZA, no un renglón de texto —
                  ADR-2026-08-31-el-desplegable-de-modos-muestra-lo-que-esconde: pila de
                  carátulas de lo que hay adentro, el rótulo de la familia, los primeros
                  modos por nombre y el chevron en su pastilla. Cerrado tiene que dejar
                  ver qué se está escondiendo; si no, es un link disfrazado de sección.
                  El propio botón hace de encabezado: con un `h2` aparte se leía dos
                  veces el mismo rótulo. */}
              <button
                type="button"
                onClick={() => setOtraFamiliaAbierta((v) => !v)}
                aria-expanded={otraFamiliaAbierta}
                className="group flex w-full items-center gap-3.5 rounded-2xl border border-black/[0.07] bg-white px-3.5 py-3 text-left shadow-[0_1px_2px_rgba(16,24,45,0.04)] transition-colors hover:border-black/[0.13] hover:bg-black/[0.015] sm:gap-4 sm:px-5 sm:py-4"
              >
                {/* Pila de carátulas, superpuestas y con reborde blanco: se lee como
                    "hay un mazo de modos acá abajo" en un vistazo, sin ocupar una fila
                    de cards. `aria-hidden` porque los nombres ya están en el texto. */}
                <div className="flex shrink-0 -space-x-3">
                  {miniaturasOtra.map((src, i) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      aria-hidden="true"
                      loading="lazy"
                      style={{ zIndex: miniaturasOtra.length - i }}
                      className="relative h-11 w-11 rounded-xl border-2 border-white bg-neutral-100 object-cover shadow-sm sm:h-12 sm:w-12"
                    />
                  ))}
                </div>
                <div className="min-w-0 flex-1">
                  <span className="block text-[16px] font-semibold leading-tight tracking-[-0.01em] text-brand-black sm:text-[18px]">
                    {rotuloOtra}
                  </span>
                  <span className="mt-0.5 block truncate text-[12.5px] text-[#3C3C43]/60 sm:text-[13px]">
                    {resumenOtra}
                  </span>
                </div>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-black/[0.04] text-brand-black/55 transition-colors group-hover:bg-black/[0.08] group-hover:text-brand-black/80">
                  <ChevronDown
                    className={`h-5 w-5 transition-transform duration-300 ${
                      otraFamiliaAbierta ? "rotate-180" : ""
                    }`}
                  />
                </span>
              </button>
              {otraFamiliaAbierta && <div className="mt-6">{gridOtra}</div>}
            </section>
          )}
        </>
      ) : (
        SECCIONES_MODOS.map((seccion, iSeccion) => {
          const grid = gridDeFamilia(seccion.familia);
          // Una sección sin nada adentro no dibuja ni su título: le puede pasar a
          // "Imagen" si el rubro filtra las plantillas y el plan bloquea el resto.
          if (!grid) return null;
          return (
            <section
              key={seccion.familia}
              /* La primera lleva el aire que antes ponía la fila del título de
                 pantalla; las siguientes, la separación entre secciones. */
              className={`mb-10 last:mb-0 ${iSeccion === 0 ? "pt-2 sm:pt-4" : ""}`}
            >
              {/* Del gris chiquito en versalitas al tamaño de un título de pantalla
                  (ADR-2026-08-31-modos-se-titula-por-seccion): con dos secciones y diez
                  cards, una etiqueta de 11px al 35% de negro se perdía entre las
                  carátulas y el corte no se leía al scrollear. Es la misma escala que
                  usa el shell para los títulos de sección (32px / 700 / -0.02em), sólo
                  que escrita: la regla `.shell-seccion h1` alcanza únicamente a `h1`, y
                  acá los dos encabezados son hermanos y ninguno manda sobre el otro. */}
              <h2 className="mb-4 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-brand-black sm:text-[32px]">
                {seccion.rotulo}
              </h2>
              {grid}
            </section>
          );
        })
      )}

      <FeatureLockedModal
        open={modeLock !== null}
        onClose={() => setModeLock(null)}
        title={modeLock?.title ?? ""}
        message={modeLock?.message ?? ""}
      />

      <DesktopOnlyModal
        open={showVideoDesktopOnly}
        onClose={() => setShowVideoDesktopOnly(false)}
      />

      <ModeExamplesModal
        open={examplesModal !== null}
        onClose={() => setExamplesModal(null)}
        title={examplesModal?.title ?? ""}
        description={examplesModal?.description}
        examples={examplesModal?.examples ?? []}
        actionLabel={examplesModal?.actionLabel}
        onAction={examplesModal?.onAction}
        locked={examplesModal?.locked}
        planNote={examplesModal?.planNote}
      />
    </div>
  );
};
